// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo, useState, useContext, useEffect } from "react";
import { format } from "date-fns";
import DeliveriesTimeChart from "./Charts/DeliveriesTimeChart";
import MaterialDistributionChart from "./Charts/MaterialDistributionChart";
import DeliveryDetailsModal from "../DeliveryDetails/DeliveryDetailsModal";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { AuthContext } from "../../context/AuthContext";
import {
  getClaimSettings,
  getDeliverySettings,
} from "../../shared_components/utils/FirebaseUtils";
import { firestore } from "../../context/FirebaseConfig";
import { useTranslation } from "react-i18next";
import "./OverviewTab.scss";
import SupplierContamination from "./Charts/SupplierContamination";

function OverviewTab({
  supplier,
  supplierData,
  startDate,
  endDate,
  materialFilter = "all",
  onSupplierClick,
  chartViewMode,
  onChartViewModeChange,
}) {
  const { labelsets, currentUser } = useContext(AuthContext);
  const [claimSettings, setClaimSettings] = useState(null);
  const [deliveriesSettings, setDeliveriesSettings] = useState(null);
  const { t } = useTranslation();

  // Load claim settings
  useEffect(() => {
    if (currentUser?.company?.company) {
      getClaimSettings(firestore, currentUser.company.company).then(
        (settings) => {
          setClaimSettings(settings);
        }
      );
      getDeliverySettings(firestore, currentUser.company.company).then(
        (settings) => {
          setDeliveriesSettings(settings);
        }
      );
    }
  }, [currentUser]);

  // Get the 5 most recent deliveries for timeline display
  const recentDeliveries = useMemo(() => {
    if (!supplier || !supplierData?.deliveries) return [];

    // First filter by material if a filter is active
    let filteredDeliveries = supplierData.deliveries;
    if (materialFilter !== "all") {
      filteredDeliveries = supplierData.deliveries.filter(
        (delivery) => delivery.base_labels?.materials === materialFilter
      );
    }

    // Sort deliveries by date, newest first
    return [...filteredDeliveries]
      .sort((a, b) => {
        const dateA = a.date ? new Date(a.date.toDate()).getTime() : 0;
        const dateB = b.date ? new Date(b.date.toDate()).getTime() : 0;
        return dateB - dateA; // Descending order (newest first)
      })
      .slice(0, 5); // Take only the 5 most recent
  }, [supplierData?.deliveries, supplier, materialFilter]);

  // Get the latest delivery with a confirmed claim
  const latestIssueDelivery = useMemo(() => {
    if (!supplierData?.deliveries) return null;

    return (
      [...supplierData.deliveries]
        .filter(
          (delivery) =>
            delivery.claim_data &&
            delivery.claim_data.claim_status === "confirmed"
        )
        .sort((a, b) => {
          const dateA = a.date ? new Date(a.date.toDate()).getTime() : 0;
          const dateB = b.date ? new Date(b.date.toDate()).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        })[0] || null
    );
  }, [supplierData?.deliveries]);

  // Calculate issue trend based on real data
  const issueTrend = useMemo(() => {
    if (!supplierData?.deliveries) return "No Data";

    const deliveriesWithClaims = supplierData.deliveries.filter(
      (delivery) =>
        delivery.claim_data && delivery.claim_data.claim_status === "confirmed"
    );

    if (deliveriesWithClaims.length === 0) return "No Issues";

    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

    // Separate deliveries into recent (last 30 days) and older
    const recentClaims = deliveriesWithClaims.filter((delivery) => {
      const deliveryDate = delivery.date
        ? new Date(delivery.date.toDate())
        : null;
      return deliveryDate && deliveryDate >= thirtyDaysAgo;
    });

    const olderClaims = deliveriesWithClaims.filter((delivery) => {
      const deliveryDate = delivery.date
        ? new Date(delivery.date.toDate())
        : null;
      return deliveryDate && deliveryDate < thirtyDaysAgo;
    });

    // Calculate average claims per month for older claims
    if (olderClaims.length === 0)
      return recentClaims.length > 0 ? "Increasing" : "No Issues";

    // Find the oldest delivery date to determine the time range for older claims
    const oldestDate = olderClaims.reduce((oldest, delivery) => {
      const deliveryDate = delivery.date
        ? new Date(delivery.date.toDate())
        : null;
      if (!deliveryDate) return oldest;
      return !oldest || deliveryDate < oldest ? deliveryDate : oldest;
    }, null);

    if (!oldestDate) return "No Data";

    // Calculate months between oldest delivery and 30 days ago
    const monthsInOlderPeriod =
      (thirtyDaysAgo.getTime() - oldestDate.getTime()) /
      (30 * 24 * 60 * 60 * 1000);

    // Ensure we have at least 1 month to avoid division by zero
    const averageClaimsPerMonth =
      olderClaims.length / Math.max(1, monthsInOlderPeriod);

    // For recent claims, normalize to per month (30 days = 1 month)
    const recentClaimsPerMonth = recentClaims.length;

    // Compare recent claims rate with historical average
    const threshold = 0.2; // 20% threshold for determining significant change
    const ratio = recentClaimsPerMonth / averageClaimsPerMonth;

    if (ratio > 1 + threshold) return "Increasing";
    if (ratio < 1 - threshold && ratio > 0) return "Decreasing";
    return "Stable";
  }, [supplierData?.deliveries]);

  // Get display name for issue
  const getIssueDisplayName = (issueCode) => {
    return (
      claimSettings?.delivery_issues?.[issueCode]?.display_name || issueCode
    );
  };

  // Get formatted issue text for latest issue
  const getLatestIssueText = () => {
    if (!latestIssueDelivery || !latestIssueDelivery.claim_data)
      return t("no_issues_found");

    const issues = latestIssueDelivery.claim_data.delivery_issues || [];
    if (issues.length === 0) return t("issue_reported");

    return issues.map((issueCode) => getIssueDisplayName(issueCode)).join(", ");
  };

  // Get formatted date for latest issue
  const getLatestIssueDate = () => {
    if (!latestIssueDelivery || !latestIssueDelivery.date)
      return format(new Date(), "dd.MM.yyyy");

    const date = latestIssueDelivery.date.toDate
      ? latestIssueDelivery.date.toDate()
      : new Date(latestIssueDelivery.date);
    return format(date, "dd.MM.yyyy");
  };

  // States for the DeliveryDetailsModal
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Calculate current year for the YTD metrics
  const currentYear = new Date().getFullYear().toString();

  // Modal handlers
  const handleSelectDelivery = (deliveryId) => {
    setActiveDelivery(deliveryId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Function to check if a delivery has issues
  const deliveryHasIssues = (delivery) => {
    return delivery.claim_data && delivery.claim_data.claim_nr;
  };

  // Function to get material display name
  const getMaterialDisplayName = (materialKey) => {
    return (
      labelsets?.materials?.labels?.[materialKey]?.display_name ||
      materialKey ||
      "Unknown"
    );
  };

  return (
    <div className="tab-overview animate-fade-in">
      <SupplierContamination 
        currentSupplier={supplier} 
        onSupplierClick={onSupplierClick}
        viewMode={chartViewMode}
        onViewModeChange={onChartViewModeChange}
      />
      {/* Deliveries Over Time Chart */}
      <div className="overview-charts">
        <div className="chart-placeholder">
          <h4>{t("deliveries_over_time")}</h4>
          <DeliveriesTimeChart
            deliveries={supplierData.deliveries || []}
            startDate={startDate}
            endDate={endDate}
            materialFilter={materialFilter}
          />
        </div>
        <div className="chart-placeholder">
          <h4>{t("material_distribution")}</h4>
          <MaterialDistributionChart
            deliveries={supplierData.deliveries || []}
          />
        </div>
      </div>

      {/* Issues summary cards - shown only if there are issues */}
      {supplierData?.claimCount > 0 && (
        <div className="issues-summary">
          <div
            className="issue-card"
            onClick={() =>
              latestIssueDelivery &&
              handleSelectDelivery(latestIssueDelivery.id)
            }
            style={{ cursor: latestIssueDelivery ? "pointer" : "default" }}
          >
            <div className="issue-icon warning">
              <WarningAmberIcon className="icon" />
            </div>
            <div className="issue-content">
              <div className="issue-label">{t("latest_issue")}</div>
              <div className="issue-value">{getLatestIssueText()}</div>
              <div className="issue-date">{getLatestIssueDate()}</div>
            </div>
          </div>

          <div className="issue-card">
            <div className="issue-icon info">
              <TrendingUpIcon className="icon" />
            </div>
            <div className="issue-content">
              <div className="issue-label">{t("issue_trend")}</div>
              <div className="issue-value">{t(issueTrend.toLowerCase())}</div>
              <div className="issue-date">{t("last_30_days")}</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent deliveries timeline */}
      <div className="recent-deliveries-section">
        <h4>
          {t("recent_deliveries")}
          {materialFilter !== "all" && (
            <span className="material-filter-badge">
              {getMaterialDisplayName(materialFilter)}
            </span>
          )}
        </h4>
        <div className="deliveries-timeline">
          {recentDeliveries.length > 0 ? (
            recentDeliveries.map((delivery) => {
              const delivery_weight = parseFloat(
                delivery.documents_data?.complete_data?.weight_kg_netto_2 ||
                  delivery.documents_data?.complete_data?.weight_kg_netto_1
              );
              return (
                <div
                  key={delivery.id}
                  className="timeline-item"
                  onClick={() => handleSelectDelivery(delivery.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="timeline-icon">
                    <LocalShippingIcon className="icon" />
                  </div>
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <h5>
                        {delivery.date
                          ? format(
                              new Date(delivery.date.toDate()),
                              "dd.MM.yyyy"
                            )
                          : "N/A"}
                      </h5>
                      <span className="timeline-badge">
                        {getMaterialDisplayName(
                          delivery.base_labels?.materials
                        )}
                      </span>
                      {deliveryHasIssues(delivery) && (
                        <span className="timeline-alert">
                          <WarningAmberIcon className="icon" />{" "}
                          {t("issue_reported")}
                        </span>
                      )}
                    </div>
                    <div className="timeline-details">
                      {delivery_weight
                        ? `${(parseFloat(delivery_weight) / 1000).toFixed(
                            2
                          )} ${t("tons")}`
                        : t("unknown_weight")}
                      ,
                      {delivery.bales
                        ? ` ${Object.keys(delivery.bales).length} ${t("bales")}`
                        : t("unknown_bales")}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty-message">
              {materialFilter !== "all"
                ? t("no_deliveries_found_for_material", {
                    material: getMaterialDisplayName(materialFilter),
                  })
                : t("no_deliveries_found")}
            </div>
          )}
        </div>
      </div>

      {/* Modal for delivery details */}
      <DeliveryDetailsModal
        show={showModal}
        onHide={handleCloseModal}
        deliveryId={activeDelivery}
        deliveriesSettings={deliveriesSettings}
      />
    </div>
  );
}

export default OverviewTab;
